/**
 * Bay Hooks - React Query
 * 
 * React Query hooks for bay availability checking.
 * 
 * **NOTE**: Bay data comes from club.bays (fetched via appRuntime).
 * These hooks only handle availability queries for the booking flow.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { queryKeys } from '~/lib/query/keys';
import { cachePresets } from '~/lib/query/cachePresets';
import * as bayService from '../api/bayService';

/**
 * Parameters for available bays query
 */
export interface UseAvailableBaysQueryParams {
  clubId: number;
  startTime?: string;   // ISO 8601 in UTC (YYYY-MM-DDTHH:mm:ss.sssZ)
  endTime?: string;     // ISO 8601 in UTC (YYYY-MM-DDTHH:mm:ss.sssZ)
  userId?: number;      // For membership-aware availability
  bayIds?: number[];    // Optional bay IDs to filter (backend filtering)
  collectAllRestrictions?: boolean;  // Return all restrictions vs first blocker only
  slotIntervalMinutes?: number;      // Effective slot interval used by client (min 30 in web portal)
  type?: 'demo';        // Demo flow: answer from the demo window (S3)
  enabled?: boolean;    // Optional: disable query
}

/**
 * Hook to fetch available bays for a club with duration-based checking
 * 
 * Fetches bays with availability status for an entire booking duration.
 * Returns the new API format with full type safety (ApiResult<DurationBayAvailabilityPayload>).
 * 
 * Returns the full React Query result with convenience `result` and `bays` fields.
 * 
 * @param params - Query parameters (clubId, startTime, endTime, userId, bayIds)
 * @returns React Query result with DurationBayAvailabilityPayload
 * 
 * @example
 * ```typescript
 * // Simple usage - convenience fields
 * function BaySelection() {
 *   const { result, bays, isLoading } = useAvailableBaysQuery({
 *     clubId: club.id,
 *     startTime: '2024-03-15T20:00:00.000Z', // UTC
 *     endTime: '2024-03-15T22:00:00.000Z',   // UTC
 *     userId: user?.id,
 *   });
 *   
 *   if (isLoading) return <Spinner />;
 *   if (!result?.success) return <Error message={result?.message} />;
 *   
 *   const available = bays.filter(b => b.isAvailable);
 *   
 *   return (
 *     <div>
 *       {available.map(bay => (
 *         <BayCard
 *           key={bay.id}
 *           name={bay.name}
 *           isAvailable={bay.isAvailable}
 *           chips={bay.chips}
 *           onSelect={() => handleSelect(bay)}
 *         />
 *       ))}
 *     </div>
 *   );
 * }
 * 
 * // Advanced usage - React Query features
 * const { bays, isFetching, refetch } = useAvailableBaysQuery({...});
 * ```
 */
export function useAvailableBaysQuery({
  clubId,
  startTime,
  endTime,
  userId,
  bayIds,
  collectAllRestrictions,
  slotIntervalMinutes,
  type,
  enabled = true,
}: UseAvailableBaysQueryParams) {
  const query = useQuery({
    queryKey: clubId
      ? queryKeys.bays.available(clubId, { startTime, endTime, userId, bayIds, collectAllRestrictions, slotIntervalMinutes, type })
      : queryKeys.disabled.bayAvailable(),
    queryFn: () => bayService.getAvailableBays(clubId, { startTime, endTime, userId, bayIds, collectAllRestrictions, slotIntervalMinutes, type }),
    enabled: enabled && !!clubId && !!startTime && !!endTime,

    // Keep the previous bay list on screen while a new range loads. Without it
    // `availableBays` goes undefined mid-fetch, `selectedBay` derives to null, and
    // BOTH BookingTicketCard mounts unmount — so changing the booking length blanks
    // the summary card for a 300ms debounce plus a round-trip. The grid's query has
    // carried this for the same reason; the bay list was simply missed.
    placeholderData: keepPreviousData,

    // Real-time availability data - bays list is relatively stable but refetch when user returns
    ...cachePresets.realTime,
  });

  return {
    ...query,
    result: query.data ?? null,
    bays: (query.data?.success ? query.data.payload?.items : []) ?? [],
  };
}

/**
 * Parameters for club availability query (unified all bays or specific bay)
 */
export interface UseClubAvailabilityQueryParams {
  clubId: number;
  date: string;        // YYYY-MM-DD format
  bayId?: number;      // Optional: filter to specific bay
  durations?: string;  // Optional: comma-separated durations
  slotInterval?: number; // Optional: override grid step (minutes). E.g. 30 forces 30-min cells even if club is set to 15.
  userId?: number;     // Optional: user ID for subscription check
  type?: 'demo';       // Demo flow: answer from the demo window (S3)
  enabled?: boolean;   // Optional: disable query
}

/**
 * Hook to fetch club availability with states (all bays or specific bay)
 * 
 * Unified hook that can fetch availability for all bays or a specific bay.
 * This replaces the need for separate hooks for different use cases.
 * 
 * When bayId is omitted, returns aggregated availability across all bays
 * (a slot is available if ANY bay is available). When bayId is provided,
 * returns availability for that specific bay only.
 * 
 * Returns the full React Query result with a convenience `availability` field.
 * 
 * @param params - Query parameters (clubId, date, optional bayId)
 * @returns React Query result with availability data
 * 
 * @example
 * ```typescript
 * // Simple usage - convenience field
 * // Get availability across all bays
 * const { availability, isLoading } = useClubAvailabilityQuery({
 *   clubId: club.id,
 *   date: '2024-01-15',
 *   durations: '60,90,120',
 * });
 * 
 * // Get availability for specific bay
 * const { availability } = useClubAvailabilityQuery({
 *   clubId: club.id,
 *   date: '2024-01-15',
 *   bayId: selectedBayId,
 *   durations: '60,90,120',
 * });
 * 
 * // Advanced usage - React Query features
 * const { availability, isFetching, dataUpdatedAt } = useClubAvailabilityQuery({...});
 * ```
 */
export function useClubAvailabilityQuery({
  clubId,
  date,
  bayId,
  durations,
  slotInterval,
  userId,
  type,
  enabled = true,
}: UseClubAvailabilityQueryParams) {
  const query = useQuery({
    // Use centralized query keys with club-scoped pattern for automatic invalidation
    queryKey: clubId && date
      ? queryKeys.bays.clubAvailability(clubId, date, bayId, durations, userId, slotInterval, type)
      : queryKeys.disabled.clubAvailability(),
    queryFn: () => bayService.getClubAvailability(clubId, {
      date,
      bayId,
      durations,
      slotInterval,
      userId,
      type,
    }),
    enabled: enabled && !!clubId && !!date,

    // Keep the previous grid on screen while a new one loads. A DATE or BAY change is
    // a new key — without this the data goes undefined mid-fetch and the caller renders
    // its empty state ("no available time slots") for the width of a round-trip, which
    // reads as "this date is unbookable" rather than "loading".
    //
    // A duration change is no longer a new key: `useAvailabilitySelection` asks for every
    // length at once and filters client-side, so switching length costs no request at all.
    placeholderData: keepPreviousData,

    // Real-time availability data - always fresh, refetch on window focus
    ...cachePresets.realTime,

    // `realTime`'s 30s gcTime, overridden HERE rather than in the preset — the preset is
    // shared with the door-unlock link and club status, and `cachePresets.test.ts` pins
    // its three values.
    //
    // The step machine is why: `BookingStepManager` uses AnimatePresence `mode="wait"`,
    // which UNMOUNTS step 0 on the way to payment. A member who spends more than 30s
    // there comes back to an evicted entry, so there is no placeholder to sit behind and
    // `isLoading` flips true — the grid drops to its loading skeleton and the previous
    // grid is lost. Ten minutes keeps the entry alive across that round trip;
    // `staleTime: 0` still refetches, so nothing is served stale.
    gcTime: 10 * 60 * 1000,
  });

  return {
    ...query,
    availability: query.data ?? null,
  };
}
